import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Layers, Images, Info, Sparkles, Newspaper, PlayCircle, BarChart3, Trophy } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { isFeatureEnabled } from '../constants';

/**
 * Home page - Landing page with search and feature cards
 * Inspired by modern new tab page experiences
 */
export default function Home() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/gallery?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/gallery');
    }
  };

  const featureCards = [
    {
      title: t('navigation.deckBuilder') || 'Deck Builder',
      description: 'Build and analyze your Ultraman card decks with statistics and hand simulation',
      icon: Layers,
      path: '/deck-builder',
      gradient: 'from-blue-500 to-indigo-600',
    },
    // Behind the same flag as the nav entries, so the flag turns the whole feature
    // off in one place rather than leaving dead links on the landing page.
    ...(isFeatureEnabled('META_ANALYTICS') ? [
      {
        title: t('navigation.metaAnalytics'),
        description: t('home.metaAnalytics'),
        icon: BarChart3,
        path: '/meta',
        gradient: 'from-sky-500 to-cyan-600',
      },
      {
        title: t('navigation.deckDatabase'),
        description: t('home.deckDatabase'),
        icon: Trophy,
        path: '/decks/tournament',
        gradient: 'from-violet-500 to-fuchsia-600',
      },
    ] : []),
    {
      title: t('navigation.cardGallery') || 'Card Gallery',
      description: 'Browse and explore the complete card database',
      icon: Images,
      path: '/gallery',
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      title: t('navigation.about') || 'About',
      description: 'Learn more about this fan-made deck building tool',
      icon: Info,
      path: '/about',
      gradient: 'from-emerald-500 to-teal-600',
    },
    {
      title: t('navigation.blog') || 'Blog',
      description: 'Strategy guides, card spotlights, and game news',
      icon: Newspaper,
      path: '/blog',
      gradient: 'from-amber-500 to-orange-600',
    },
    {
      title: t('navigation.videos') || 'Videos',
      description: 'Deck profiles, set reviews, and gameplay videos',
      icon: PlayCircle,
      path: '/videos',
      gradient: 'from-red-500 to-rose-600',
    },
  ];

  return (
    <div className="min-h-[calc(100vh-48px)] bg-gradient-to-b from-background to-muted/30">
      {/* Header Section with Gradient */}
      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 text-center">
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight mb-2 uppercase"
            style={{ fontFamily: 'Lato, Arial, sans-serif' }}
          >
            Gardeno Games
          </h1>
          <p className="text-white/80 text-lg">
            {t('header.subtitle') || 'Ultraman Card Game Deck Builder & Database'}
          </p>
        </div>
      </div>

      {/* Search Section */}
      <div className="flex flex-col items-center pt-10 pb-12 px-4">

        {/* Search Box */}
        <form onSubmit={handleSearch} className="w-full max-w-2xl mb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('sidebar.search.placeholder') || 'Search for cards...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-14 pl-12 pr-4 text-lg rounded-full border-2 shadow-lg focus-visible:ring-primary"
            />
            <Button 
              type="submit"
              size="sm"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-6"
            >
              {t('common.search') || 'Search'}
            </Button>
          </div>
        </form>

        <p className="text-sm text-muted-foreground">
          Search by card name, character, or type
        </p>
      </div>

      {/* Feature Cards */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featureCards.map((feature) => (
            <Card
              key={feature.path}
              className="group cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-0"
              onClick={() => navigate(feature.path)}
            >
              {/* Card Header with Gradient */}
              <div className={`h-32 bg-gradient-to-br ${feature.gradient} flex items-center justify-center relative overflow-hidden`}>
                <feature.icon className="h-16 w-16 text-white/90 transition-transform group-hover:scale-110" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              
              {/* Card Content */}
              <CardContent className="p-5">
                <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Stats or Recent Activity could go here */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-muted-foreground text-sm">
            <Sparkles className="h-4 w-4" />
            <span>Fan-made tool for the Ultraman Card Game community</span>
          </div>
        </div>
      </div>
    </div>
  );
}
